import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subject, Subscription, catchError, from, map, of, switchMap, take, takeUntil, timeout, timer } from 'rxjs';
import { UserService } from 'src/app/services/user.service';
import { LocationLookupService, LocationSelection, LocationSuggestion, locationError, validLocation } from 'src/app/services/location-lookup.service';

@Component({
  selector: 'app-google-location-search',
  templateUrl: './google-location-search.component.html',
  styleUrls: ['./google-location-search.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class GoogleLocationSearchComponent implements OnInit, OnDestroy {
  @Input() isEdit = false;
  @Input() addressOnboarding = '';
  @Input() cityOnboarding = '';
  @Input() businessDetaillat = 0;
  @Input() businessDetaillng = 0;
  searchControl = new FormControl('', { nonNullable: true });
  suggestions: LocationSuggestion[] = [];
  selectedLocation: LocationSelection | null = null;
  mapsReady = false;
  mapError = '';
  searchError = '';
  searching = false;
  resolving = false;
  initialCoordinates = { lat: 46.533408, lng: 8.352592 };
  zoom = 3;
  mapOptions: google.maps.MapOptions = { mapTypeControl: false };
  markerPosition: google.maps.LatLngLiteral | null = null;
  markerOptions: google.maps.MarkerOptions = {};
  private readonly destroyed$ = new Subject<void>();
  private readonly searchCancelled$ = new Subject<void>();
  private coordinateRequest?: Subscription;
  private mapRequest?: Subscription;
  private revision = 0;
  private destroyed = false;

  constructor(private userService: UserService, private lookup: LocationLookupService) {}

  ngOnInit(): void {
    this.searchControl.valueChanges.pipe(
      map(value => value.trim()),
      switchMap(query => {
        this.invalidateSelection();
        this.suggestions = [];
        this.searchError = '';
        this.searching = query.length >= 3;
        if (!this.searching) return of([]);
        return timer(300).pipe(
          switchMap(() => this.lookup.search(query)),
          catchError(error => { this.searchError = locationError(error); return of([]); }),
          takeUntil(this.searchCancelled$)
        );
      }),
      takeUntil(this.destroyed$)
    ).subscribe(suggestions => { this.suggestions = suggestions; this.searching = false; });

    if (this.isEdit) {
      this.userService.searchLocation$.pipe(take(1)).subscribe(locations => {
        const current = locations?.[0];
        if (validLocation(current)) this.choose(current);
      });
    } else {
      const address = [this.addressOnboarding, this.cityOnboarding]
        .filter(value => value && !/^(undefined|null)(, (undefined|null))*$/.test(value)).join(', ');
      const latitude = Number(this.businessDetaillat), longitude = Number(this.businessDetaillng);
      const current = { address, latitude, longitude };
      if (this.businessDetaillat != null && this.businessDetaillng != null
        && (latitude !== 0 || longitude !== 0) && validLocation(current)) this.choose(current);
      else if (address) this.searchControl.setValue(address);
    }
    this.loadMap();
  }

  loadMap(): void {
    this.mapRequest?.unsubscribe();
    this.mapError = '';
    this.mapRequest = this.lookup.waitForMaps().pipe(takeUntil(this.destroyed$)).subscribe({
      next: () => {
        this.mapsReady = true;
        this.markerOptions = { icon: { url: '../assets/images/location-select-profile.svg',
          scaledSize: new google.maps.Size(50, 50) } };
        if (!this.searchControl.value && !this.selectedLocation && this.revision === 0) this.getCurrentLocation();
      },
      error: () => { this.mapError = 'The map could not load. Please try again.'; }
    });
  }

  onLocationSelect(suggestion: LocationSuggestion): void {
    this.searchCancelled$.next();
    this.invalidateSelection();
    this.suggestions = [];
    this.searching = false;
    this.searchError = '';
    this.searchControl.setValue(suggestion.description, { emitEvent: false });
    this.resolving = true;
    this.resolve(this.lookup.coordinates(suggestion), this.revision);
  }

  private getCurrentLocation(): void {
    const revision = this.revision;
    this.resolving = true;
    this.resolve(from(this.userService.getCurrentPosition()).pipe(
      timeout(10000), switchMap(position => this.lookup.reverse({ lat: position.coords.latitude, lng: position.coords.longitude }))
    ), revision, true);
  }

  private resolve(request: ReturnType<LocationLookupService['coordinates']>, revision: number, automatic = false): void {
    this.coordinateRequest = request.pipe(takeUntil(this.destroyed$)).subscribe({
      next: location => { if (revision === this.revision && !this.destroyed) this.choose(location); },
      error: error => {
        if (revision !== this.revision || this.destroyed) return;
        this.resolving = false;
        this.searchError = automatic ? 'Search for your business address to select a location.' : locationError(error);
      }
    });
  }

  private choose(location: LocationSelection): void {
    this.selectedLocation = { ...location };
    this.searchControl.setValue(location.address || '', { emitEvent: false });
    this.initialCoordinates = { lat: location.latitude, lng: location.longitude };
    this.markerPosition = { ...this.initialCoordinates };
    this.zoom = 17;
    this.resolving = false;
  }

  private invalidateSelection(): void {
    this.revision++;
    this.coordinateRequest?.unsubscribe();
    this.selectedLocation = null;
    this.markerPosition = null;
    this.resolving = false;
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.destroyed$.next(); this.destroyed$.complete();
    this.searchCancelled$.complete();
  }
}
